import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  User, Lock, Bell,
} from 'lucide-react';
import { apiGet, apiPatch } from '../../api/client';
import { AvatarUpload } from '../../components/ui/AvatarUpload';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import TwoFactorSettings from '../../components/settings/TwoFactorSettings';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../utils/format';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProfileUser {
  id: string;
  name: string;
  email: string;
  role: string;
  bio?: string | null;
  avatarUrl?: string | null;
  ntfyTopic?: string | null;
  professionalRole?: string | null;
  authProvider: string;
  hasGoogleCalendar: boolean;
  linkedCollaboratorId?: string | null;
  linkedExternalPhotographerId?: string | null;
  createdAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  overflow: 'hidden',
};

const sectionHeaderStyle: React.CSSProperties = {
  padding: '0.875rem 1.25rem',
  background: 'rgba(255,255,255,0.04)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  color: 'var(--color-text)',
  fontSize: 'var(--text-sm)',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-xs)',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  marginBottom: '0.3rem',
};

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div style={sectionHeaderStyle}>
      <span style={{ color: 'var(--color-primary)' }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>{title}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user: storeUser, setUser } = useAuthStore();

  const { data: profile, isLoading } = useQuery<ProfileUser>({
    queryKey: ['profile-me'],
    queryFn: () => apiGet<ProfileUser>('/auth/me'),
  });

  // ── Form state ──────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [ntfyTopic, setNtfyTopic] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [professionalRole, setProfessionalRole] = useState('');

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? '');
      setEmail(profile.email ?? '');
      setBio(profile.bio ?? '');
      setNtfyTopic(profile.ntfyTopic ?? '');
      setProfessionalRole(profile.professionalRole ?? '');
      setAvatarUrl(profile.avatarUrl ?? null);
    }
  }, [profile]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const updateProfile = useMutation({
    mutationFn: (data: object) => apiPatch('/auth/profile', data),
    onSuccess: (res: unknown) => {
      const updated = res as Partial<ProfileUser>;
      if (storeUser) setUser({ ...storeUser, ...updated });
      qc.invalidateQueries({ queryKey: ['profile-me'] });
      toast.success('Profilo aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveInfo = () => {
    if (!name.trim()) { toast.error('Il nome è obbligatorio'); return; }
    updateProfile.mutate({ name: name.trim(), email: email.trim(), bio: bio.trim() || null, professionalRole: professionalRole.trim() || null });
  };

  const savePassword = () => {
    if (!currentPw || !newPw) { toast.error('Inserisci password attuale e nuova'); return; }
    if (newPw !== confirmPw) { toast.error('Le password non coincidono'); return; }
    if (newPw.length < 6) { toast.error('La password deve avere almeno 6 caratteri'); return; }
    updateProfile.mutate(
      { currentPassword: currentPw, newPassword: newPw },
      { onSuccess: () => { setCurrentPw(''); setNewPw(''); setConfirmPw(''); } },
    );
  };

  const saveNotifications = () => {
    updateProfile.mutate({ ntfyTopic: ntfyTopic.trim() || null });
  };

  const handleAvatarSuccess = (url: string | null) => {
    setAvatarUrl(url);
    if (storeUser) setUser({ ...storeUser, avatarUrl: url });
    qc.invalidateQueries({ queryKey: ['profile-me'] });
  };

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Caricamento profilo...</div>
      </div>
    );
  }

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      <PageHeader title="Profilo" subtitle="Gestisci le tue informazioni personali, sicurezza e preferenze." />

      {/* ── Sezione: Informazioni personali ────────────────────────────────── */}
      <div style={sectionStyle}>
        <SectionHeader icon={<User size={16} />} title="Informazioni personali" />
        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          <AvatarUpload
            currentAvatar={avatarUrl}
            name={name}
            onSuccess={handleAvatarSuccess}
          />

          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
            <Field label="Nome">
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Email">
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" style={inputStyle} />
            </Field>
          </div>

          <Field label="Bio">
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              maxLength={500}
              placeholder="Una breve descrizione di te..."
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.2rem' }}>
              {bio.length}/500 caratteri
            </p>
          </Field>

          <Field label="Ruolo professionale">
            <input
              value={professionalRole}
              onChange={e => setProfessionalRole(e.target.value)}
              placeholder="es. Fotografo, Videomaker..."
              style={inputStyle}
            />
          </Field>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{
              padding: '0.375rem 0.75rem',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
            }}>
              Ruolo: <strong style={{ color: 'var(--color-text)' }}>{profile?.role}</strong>
            </div>
            <div style={{
              padding: '0.375rem 0.75rem',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
            }}>
              Membro dal: <strong style={{ color: 'var(--color-text)' }}>
                {profile?.createdAt ? formatDate(new Date(profile.createdAt), 'MMM yyyy') : '—'}
              </strong>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={saveInfo} loading={updateProfile.isPending}>Salva informazioni</Button>
          </div>
        </div>
      </div>

      {/* ── Sezione: Sicurezza ─────────────────────────────────────────────── */}
      {profile?.authProvider === 'LOCAL' && (
        <div style={sectionStyle}>
          <SectionHeader icon={<Lock size={16} />} title="Sicurezza" />
          <form onSubmit={e => { e.preventDefault(); savePassword(); }} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            <Field label="Password attuale">
              <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                style={inputStyle} placeholder="••••••••" />
            </Field>
            <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
              <Field label="Nuova password">
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  style={inputStyle} placeholder="min. 6 caratteri" />
              </Field>
              <Field label="Conferma password">
                <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                  style={inputStyle} placeholder="ripeti la password" />
              </Field>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" loading={updateProfile.isPending}>Cambia password</Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Sezione: 2FA (tutti) ───────────────────────────────────────── */}
      <TwoFactorSettings />

      {/* ── Sezione: Notifiche ─────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <SectionHeader icon={<Bell size={16} />} title="Notifiche personali" />
        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Configura un topic ntfy personale per ricevere notifiche direttamente sul tuo dispositivo.
          </p>
          <Field label="Topic ntfy personale">
            <input
              value={ntfyTopic}
              onChange={e => setNtfyTopic(e.target.value)}
              placeholder="es. mio-topic-privato"
              style={inputStyle}
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={saveNotifications} loading={updateProfile.isPending}>Salva</Button>
          </div>
        </div>
      </div>

    </div>
  );
}
